/**
 * AwaitExternalConfirm — iter303aj-fix11
 * ──────────────────────────────────────
 * Confirm-Appraiser Action 3 renderer. Two paths:
 *   1. Auto-reply monitor (MOCKED today) — polls Twilio/IMAP for the
 *      appraiser's reply. Will activate when SMS + email credentials
 *      land. For now, shows a friendly placeholder.
 *   2. Manual operator override — operator ticks "confirmed" (optionally
 *      with a new time if the appraiser requested a reschedule). Works
 *      today. Fully-manual mode until auto-poll comes online.
 *
 * Writes to captured_data:
 *   • confirm_source        — 'auto_sms' | 'auto_email' | 'manual_phone'
 *                             | 'manual_email' | 'manual_override'
 *   • confirm_status        — 'confirmed_original' | 'confirmed_new_time'
 *   • confirmed_at          — ISO timestamp
 *   • appraiser_new_time    — string (only when confirmed_new_time)
 *   • time_changed          — boolean (drives the client_time_change_notice branch)
 *
 * Props (standard workflow renderer):
 *   action, capturedData, onNext, onBack, onCancel, canBack
 */
import React, { useState, useEffect } from 'react';
import { Clock, MessageCircle, Mail, Phone, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';

export default function AwaitExternalConfirm({ action, capturedData, onNext, onBack, onCancel, canBack }) {
  const params = action.params || {};
  const appraiserName = capturedData?.appraiser_pick?.name
    || capturedData?.['appraiser_pick']?.name
    || capturedData?.appraisal_record?.appraiser_name
    || 'appraiser';
  const appraiserPhone = capturedData?.appraiser_pick?.phone
    || capturedData?.appraisal_record?.appraiser_phone
    || 'phone on file';
  const appraiserEmail = capturedData?.appraiser_pick?.email
    || capturedData?.appraisal_record?.appraiser_email
    || 'email on file';

  const [state, setState] = useState(() => ({
    // 'confirmed_original' (default when no time change) | 'confirmed_new_time'
    outcome: '',
    // 'manual_phone' | 'manual_email' | 'manual_override'
    source: 'manual_phone',
    newTime: '',
    // iter303aj-fix12 · Timestamp default = now, formatted for
    // datetime-local input. Operator can edit if they heard back
    // earlier and are recording it after the fact.
    confirmedAt: (() => {
      const d = new Date();
      d.setSeconds(0, 0);
      // datetime-local input wants YYYY-MM-DDTHH:MM (no seconds, no tz).
      const pad = n => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    })(),
  }));
  const [lastPoll, setLastPoll] = useState(new Date());

  // MOCKED · once Twilio/IMAP arrive, this becomes a real poller.
  const mockRefresh = () => setLastPoll(new Date());
  useEffect(() => {
    const interval = setInterval(() => setLastPoll(new Date()), 60_000);
    return () => clearInterval(interval);
  }, []);

  const canContinue = state.outcome === 'confirmed_original'
    || (state.outcome === 'confirmed_new_time' && state.newTime.trim().length > 0);

  const handleContinue = () => {
    // iter303aj-fix12 · Prefer the operator-editable timestamp when it
    // parses; fall back to "now" so we never persist a null.
    let confirmedAtISO = new Date().toISOString();
    if (state.confirmedAt) {
      const parsed = new Date(state.confirmedAt);
      if (!isNaN(parsed.getTime())) confirmedAtISO = parsed.toISOString();
    }
    const captured = {
      confirm_source:   state.source,
      confirm_status:   state.outcome,
      confirmed_at:     confirmedAtISO,
      time_changed:     state.outcome === 'confirmed_new_time',
    };
    if (state.outcome === 'confirmed_new_time') {
      captured.appraiser_new_time = state.newTime.trim();
    }
    onNext(captured);
  };

  // iter303aj-fix12 · Human-readable summary of what will be captured.
  const sourceLabels = {
    manual_phone:    'Phone',
    manual_email:    'Email',
    manual_override: 'In person / other',
    auto_sms:        'Auto SMS',
    auto_email:      'Auto Email',
  };
  const summaryTimestamp = (() => {
    if (!state.confirmedAt) return '';
    const d = new Date(state.confirmedAt);
    if (isNaN(d.getTime())) return state.confirmedAt;
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  })();

  return (
    <div data-testid="wf-await-external-confirm">
      <div className="bg-gradient-to-br from-amber-50 to-white border-l-4 border-amber-400 rounded-r-lg p-5 mb-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-700 font-semibold mb-2">
          <Clock size={12} /> Awaiting appraiser confirmation
        </div>
        <div className="text-slate-700 text-sm">
          {params.prompt || 'Waiting for the appraiser to confirm the booking. Auto-poll will activate when Twilio + email credentials come online. Meanwhile, use the manual override below when you hear back out-of-band.'}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-4 text-xs" data-testid="wf-await-appraiser-details">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Appraiser</div>
            <div className="text-slate-700 font-medium">{appraiserName}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">SMS to</div>
            <div className="text-slate-700 font-medium">{appraiserPhone}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Email to</div>
            <div className="text-slate-700 font-medium truncate">{appraiserEmail}</div>
          </div>
        </div>
      </div>

      {/* Auto-poll placeholder — activates when creds land. */}
      <div className="mb-5 border border-slate-200 rounded-lg bg-slate-50 p-4" data-testid="wf-await-auto-monitor">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Auto-reply monitor</div>
          <span className="text-[9px] text-amber-800 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5 font-semibold uppercase">
            MOCKED · awaiting creds
          </span>
          <button onClick={mockRefresh}
            data-testid="wf-await-refresh"
            className="ml-auto text-xs text-slate-500 hover:text-sky-600 flex items-center gap-1">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        <div className="text-xs text-slate-500">
          Waiting for SMS reply or email confirmation from <span className="font-semibold text-slate-700">{appraiserName}</span>.
          Once Twilio + IMAP credentials are configured, this monitor will auto-tick when the appraiser replies YES (or with a proposed new time).
        </div>
        <div className="text-[10px] text-slate-400 mt-2">Last checked: {lastPoll.toLocaleTimeString()}</div>
      </div>

      {/* Manual override — the working path today. */}
      <div className="mb-5 border-2 border-sky-300 rounded-lg bg-sky-50/50 p-4" data-testid="wf-await-manual-override">
        <div className="text-[10px] uppercase tracking-widest text-sky-700 font-semibold mb-3 flex items-center gap-2">
          <CheckCircle2 size={12} /> Manual override — heard back out-of-band?
        </div>

        {/* Source picker */}
        <div className="mb-4">
          <div className="text-[11px] text-slate-500 mb-2">How did the appraiser confirm?</div>
          <div className="flex gap-2 flex-wrap" data-testid="wf-await-source-picker">
            {[
              { key: 'manual_phone',    label: 'By phone',    Icon: Phone         },
              { key: 'manual_email',    label: 'By email',    Icon: Mail          },
              { key: 'manual_override', label: 'In person / other', Icon: MessageCircle },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => setState(s => ({ ...s, source: key }))}
                data-testid={`wf-await-source-${key}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded border flex items-center gap-1.5 transition ${
                  state.source === key
                    ? 'bg-sky-600 text-white border-sky-600'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-sky-300'
                }`}>
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>
        </div>

        {/* Outcome picker */}
        <div>
          <div className="text-[11px] text-slate-500 mb-2">What did the appraiser say?</div>
          <div className="space-y-2">
            <label className="flex items-start gap-2 p-2.5 border border-slate-200 rounded bg-white hover:border-sky-300 cursor-pointer"
              data-testid="wf-await-outcome-original">
              <input type="radio" name="wf-await-outcome"
                checked={state.outcome === 'confirmed_original'}
                onChange={() => setState(s => ({ ...s, outcome: 'confirmed_original', newTime: '' }))}
                className="mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-slate-800">Confirmed at the preferred time</div>
                <div className="text-xs text-slate-500">Original booking sticks — no client re-liaise needed.</div>
              </div>
            </label>
            <label className="flex items-start gap-2 p-2.5 border border-slate-200 rounded bg-white hover:border-amber-300 cursor-pointer"
              data-testid="wf-await-outcome-new-time">
              <input type="radio" name="wf-await-outcome"
                checked={state.outcome === 'confirmed_new_time'}
                onChange={() => setState(s => ({ ...s, outcome: 'confirmed_new_time' }))}
                className="mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  Confirmed with a NEW time
                  <span className="text-[9px] font-semibold text-amber-700 bg-amber-100 border border-amber-200 rounded px-1 py-0.5">
                    <AlertTriangle size={9} className="inline mr-0.5" /> Time-change notice sent to client
                  </span>
                </div>
                <div className="text-xs text-slate-500">Appraiser proposed a different time. We&apos;ll auto-send the client a time-change letter.</div>
                {state.outcome === 'confirmed_new_time' && (
                  <input type="text"
                    value={state.newTime}
                    onChange={e => setState(s => ({ ...s, newTime: e.target.value }))}
                    placeholder="e.g. Tuesday 2pm, or Fri 11 Feb 10am"
                    data-testid="wf-await-new-time-input"
                    className="mt-2 w-full text-sm px-2 py-1.5 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-amber-300" />
                )}
              </div>
            </label>
          </div>
        </div>

        {/* iter303aj-fix12 · Confirmation timestamp — defaults to now,
            editable in case operator is entering it after the fact.
            Recorded to captured_data.confirmed_at as an ISO string. */}
        <div className="mt-4">
          <div className="text-[11px] text-slate-500 mb-2">When was this confirmed?</div>
          <input
            type="datetime-local"
            value={state.confirmedAt}
            onChange={e => setState(s => ({ ...s, confirmedAt: e.target.value }))}
            data-testid="wf-await-confirmed-at"
            className="w-full max-w-xs text-sm px-3 py-1.5 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-sky-300" />
          <div className="text-[10px] text-slate-400 mt-1">Defaults to now — edit if you heard back earlier and are recording it now.</div>
        </div>

        {/* iter303aj-fix12 · Read-back panel — makes the audit trail
            explicit before the operator commits. */}
        {state.outcome && (
          <div className="mt-4 border-l-2 border-sky-500 bg-white rounded-r px-3 py-2 text-xs" data-testid="wf-await-summary">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Will be recorded as:</div>
            <div className="text-slate-700">
              Confirmed via <span className="font-semibold text-sky-700">{sourceLabels[state.source] || state.source}</span>
              {' '}on <span className="font-semibold text-sky-700">{summaryTimestamp || '—'}</span>
              {state.outcome === 'confirmed_new_time' && state.newTime.trim() && (
                <> · new time: <span className="font-semibold text-amber-700">{state.newTime.trim()}</span></>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Buttons */}
      <div className="flex items-center gap-3">
        {canBack && (
          <button onClick={onBack} data-testid="wf-back-btn"
            className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>
        )}
        {onCancel && (
          <button onClick={onCancel} data-testid="wf-cancel-btn"
            className="px-4 py-2 text-rose-500 hover:text-rose-700 text-sm">Cancel</button>
        )}
        <span className="flex-1" />
        <button
          onClick={handleContinue}
          disabled={!canContinue}
          data-testid="wf-await-continue-btn"
          className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700 disabled:opacity-40 disabled:cursor-not-allowed">
          Continue →
        </button>
      </div>
    </div>
  );
}
